import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import { importBucketApi, useGetImportBucketGetImportBucketMutation } from '@redux/apiSlices/importBucketApi';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SelectFileTsoPq from './SelectFile';
import UploadingFilesTsoPq from './UploadingFIles';

type UploadFilesTsoPqProps = {
  openModal: boolean;
  handleModalState: (state: boolean) => void;
};

export type UploadStep = 'selectFile' | 'uploadingFiles';

importBucketApi.enhanceEndpoints({
  endpoints: {
    getImportBucketGetImportBucket: {
      query: (queryArg) => ({
        url: `/ImportBucket/get-import-bucket`,
        method: 'GET',
        params: { fileName: queryArg },
        responseHandler: (response) => response.text(),
        cache: 'no-cache',
      }),
    },
  },
});

const UploadFilesTsoPq = ({ openModal, handleModalState }: UploadFilesTsoPqProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [uploadStep, setUploadStep] = useState<UploadStep>('selectFile');
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [errorFiles, setErrorFiles] = useState<File[]>([]);

  const { t } = useTranslation();

  const [getUploadUrl] = useGetImportBucketGetImportBucketMutation();

  useEffect(() => {
    setOpen(openModal);
    setUploadStep('selectFile');
    setUploadingFiles([]);
  }, [openModal]);

  const handleFiles = (files: File[]) => {
    setUploadingFiles([...uploadingFiles, ...files]);
  };

  const removeFile = (fileToRemove: File) => {
    setUploadingFiles((prev) =>
      prev.filter((file) => !(file.name === fileToRemove.name && file.lastModified === fileToRemove.lastModified))
    );
  };

  const handleUploads = async () => {
    setUploadStep('uploadingFiles');

    try {
      for (const [index, file] of uploadingFiles.entries()) {
        const response = await getUploadUrl(file.name).unwrap();
        if (typeof response === 'string') {
          await fetch(response, {
            method: 'PUT',
            body: file,
          });
        } else {
          throw new Error('Unexpected response type: expected a string');
        }
        setCurrentFileIndex(index + 1);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setErrorFiles(uploadingFiles);
    }
  };

  return (
    <ModalDialogTsoPq
      modalState={open}
      title={t('upload.fileUpload.title')}
      buttons
      actions={
        <>
          {uploadStep === 'selectFile' && (
            <>
              <ButtonTsoPq
                buttonType="outlinedPrimary"
                textContent={t('upload.fileUpload.cancel')}
                onClick={() => {
                  handleModalState(false);
                  setUploadingFiles([]);
                }}
              />
              <ButtonTsoPq
                buttonType="filledPrimary"
                textContent={t('upload.fileUpload.upload')}
                onClick={() => uploadingFiles.length > 0 && handleUploads()}
              />
            </>
          )}
        </>
      }
    >
      {uploadStep === 'selectFile' && (
        <SelectFileTsoPq handleFiles={handleFiles} removeFile={removeFile} uploadingFiles={uploadingFiles} />
      )}
      {uploadStep === 'uploadingFiles' && (
        <UploadingFilesTsoPq
          errorFiles={errorFiles}
          currentFileIndex={currentFileIndex}
          totalFiles={uploadingFiles.length}
          handleModalState={handleModalState}
        />
      )}
    </ModalDialogTsoPq>
  );
};

export default UploadFilesTsoPq;
